// @oasis-permission-inherits: /session-replays (jerboa:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { fetchSessionReplaysQuery } from "~/modules/session-replays/queries";

export const Route = createFileRoute("/_dash/session-replays/")({ component: RouteComponent });

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    fetchSessionReplaysQuery.useInfiniteQuery({ params: {} });
  const replays = useMemo(() => data?.pages.flatMap((page) => page.page) ?? [], [data?.pages]);

  if (error) {
    return <div className="px-6 py-4 text-sm text-destructive">{error.message}</div>;
  }

  return (
    <div className="space-y-4 px-6 py-4">
      <div>
        <h1 className="text-2xl font-bold">Studio Session Replays</h1>
        <p className="text-sm text-muted-foreground">
          Full DOM playback with synchronized preview and timeline canvas video. Data expires after
          30 days.
        </p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Created</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Segments</TableHead>
            <TableHead className="text-right">Size</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : replays.map((replay) => (
                <TableRow
                  key={replay.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    void navigate({
                      to: "/session-replays/$replayId",
                      params: { replayId: replay.id },
                    })
                  }
                >
                  <TableCell className="font-mono text-xs">
                    {new Date(replay.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <span className="rounded bg-muted px-2 py-1 font-mono text-xs">
                      {replay.status}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-96 truncate font-mono text-xs">
                    {replay.sourcePath}
                  </TableCell>
                  <TableCell>{formatDuration(replay.durationMs)}</TableCell>
                  <TableCell>{replay.segmentCount}</TableCell>
                  <TableCell className="text-right">{formatBytes(replay.totalBytes)}</TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

function formatDuration(durationMs: number | null | undefined) {
  if (durationMs == null) {
    return "-";
  }
  return `${(durationMs / 1_000).toFixed(1)}s`;
}

function formatBytes(bytes: number) {
  if (bytes < 1_000_000) {
    return `${(bytes / 1_000).toFixed(1)} KB`;
  }
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
